import React, { useCallback, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { FiArrowUpRight, FiBarChart2, FiBookOpen, FiCreditCard, FiGrid, FiPieChart, FiTrendingUp } from "react-icons/fi";
import { useAuth } from "../utils";
import { fetchFinanceLedger, saveFinanceLedger } from "../api/financeLedger";
import PersonalLedger from "../components/finance/PersonalLedger";
import StockLearning from "../components/finance/StockLearning";
import FinanceLearning from "../components/finance/FinanceLearning";
import FinanceCalculators from "../components/finance/FinanceCalculators";
import BillingWorkspace from "../components/acct/BillingWorkspace";
import "../components/finance/finance.css";

const sections = [
  { id: "ledger", label: "My Ledger", icon: FiGrid },
  { id: "learn", label: "Learn", icon: FiBookOpen },
  { id: "stocks", label: "Stocks", icon: FiBarChart2 },
  { id: "bills", label: "Bills", icon: FiCreditCard },
  { id: "tools", label: "Tools", icon: FiPieChart },
];
const ledgerOwner = token => {
  try {
    const identity = jwtDecode(token).user_id;
    return typeof identity === "string" || typeof identity === "number" ? String(identity) : null;
  } catch { return null; }
};
export default function AccountingPage({ data, sendAccountingData, onSaved, error, onRetry }) {
  const [section, setSection] = useState("ledger");
  const { authToken } = useAuth();
  const ownerKey = ledgerOwner(authToken);
  const loadLedger = useCallback(() => fetchFinanceLedger({ authToken }), [authToken]);
  const saveLedger = useCallback((ledger, revision) => saveFinanceLedger({ authToken, data: ledger, expectedRevision: revision }), [authToken]);
  return (
    <main className="finance-page">
      <header className="finance-heading">
        <div><span className="finance-eyebrow">MONEY, ONE STEP AT A TIME</span><h1>財經<span className="finance-heading-dot">.</span></h1></div>
        <span className="finance-badge"><span /> 從生活開始，慢慢學會投資</span>
      </header>
      <section hidden={section !== "learn" && section !== "stocks"} className="finance-hero" aria-labelledby="finance-welcome">
        <div className="finance-hero-copy">
          <span className="finance-hero-kicker">給剛開始的你 · 財經第一課</span>
          <h2 id="finance-welcome">讓每一筆錢，<br />都有自己的方向。</h2>
          <p>先看懂收支，再認識風險。<br className="finance-mobile-break" /> 投資不用急，從今天的一小步開始。</p>
          <button className="finance-primary" onClick={() => { setSection("learn"); document.getElementById("finance-content")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>開始我的第一課 <FiArrowUpRight aria-hidden="true" /></button>
        </div>
        <div className="finance-hero-art" aria-hidden="true">
          <div className="finance-orbit" />
          <div className="finance-growth-card"><div className="finance-art-label"><FiTrendingUp /> 每天懂一點</div><strong>累積的，是底氣。</strong><svg viewBox="0 0 260 110"><path d="M10 94H250M10 58H250M10 22H250" stroke="currentColor" opacity=".1" /><path d="M10 90 C40 90 35 72 66 75 S99 56 124 62 S160 20 184 32 S220 19 248 10" fill="none" stroke="#c8d0dd" strokeWidth="4" strokeLinecap="round" /><circle cx="248" cy="10" r="6" fill="#f2f4f7" /></svg><span>認識自己 → 理解風險 → 規律實踐</span></div>
          <span className="finance-art-note">一點一滴，也是一種成長。</span>
        </div>
      </section>
      <div hidden={section !== "learn" && section !== "stocks"} className="finance-principles"><span><b>01</b> 先照顧生活</span><span><b>02</b> 只投資看得懂的</span><span><b>03</b> 給時間一點時間</span></div>
      <nav className="finance-tabs" aria-label="財經功能" id="finance-content">
        {sections.map(({ id, label, icon: Icon }) => <button key={id} aria-pressed={section === id} onClick={() => setSection(id)}><Icon aria-hidden="true" />{label}</button>)}
      </nav>
      <div hidden={section !== "ledger"}><PersonalLedger key={ownerKey || 'anonymous'} ownerKey={ownerKey} loadLedger={loadLedger} saveLedger={saveLedger} /></div>
      <div hidden={section !== "learn"}><FinanceLearning onOpenTools={() => setSection("tools")} onOpenStocks={() => setSection("stocks")} /></div>
      <div hidden={section !== "stocks"}><StockLearning /></div>
      <div hidden={section !== "bills"}>
        {error ? <section className="finance-panel finance-error" role="alert"><h2>暫時無法讀取帳單</h2><p>目前無法確認帳單內容，請重新讀取後再編輯。</p><button className="finance-primary" onClick={onRetry}>重新讀取</button></section> : <BillingWorkspace data={data} sendAccountingData={sendAccountingData} onSaved={onSaved} />}
      </div>
      <div hidden={section !== "tools"}><FinanceCalculators /></div>
      <footer className="finance-footnote">這裡提供一般財經教育與假設試算。投資可能虧損，請依自己的目標、資金需求與承受能力判斷。</footer>
    </main>
  );
}
